import { parseAccountAdditionalData, type WithAdditionalData } from "@/modules/billing/billingAccountQueryUtils.ts";
import {
    baCardQueryAtom,
    parseCardAdditionalData,
    type BillingAccountCardWithData,
} from "@/modules/billing/billingCardsQuery.ts";
import type { EBillingAccount } from "@/modules/billing/billingLogic.tsx";
import type {
    CardMethod,
    InvoiceMethod,
    LinkMethod,
    PaymentMethod,
    PaymentMethodId,
} from "@/modules/billing/paymentMethods.ts";
import { legacy_creditCardProcessorAtom, processorsAtom } from "@/modules/billing/paymentProcessorsLogic.tsx";
import { showError } from "@/modules/error/errorStream.ts";
import { atomFamilyWeak } from "@/utils/jotai/atomFamilyWeak.ts";
import { queryThen } from "@/utils/query/mergeQueries.ts";
import { useSuspenseQueryAtom } from "@/utils/query/useSuspenseQueryAtom.ts";
import type { BillingAccountCard, BillingAccountId } from "@warrenio/api-spec/spec.oats.gen";
import { filterNulls } from "@warrenio/utils/collections/filterNulls";
import { atomFamily } from "jotai-family";
import { useAtomValue } from "jotai/react";
import { atom } from "jotai/vanilla";
import invariant from "tiny-invariant";

/**
 * A bound method is a payment method bound to a specific billing account or card.
 *
 * @see {@link BoundMethodBase}
 * @see {@link useAccountMethods} to get the bound payment methods for an account
 */
export type BoundMethod = BoundOtherMethod | BoundCardMethod;

interface BoundMethodBase {
    type: unknown;
    /** Unique identifier, usable as a React key */
    id: PaymentMethodId | `__card__/${BillingAccountCard["id"]}`;

    method: PaymentMethod;
}

export interface BoundOtherMethod extends BoundMethodBase {
    type: "other";

    method: LinkMethod | InvoiceMethod;
    card?: never;
}

export interface BoundCardMethod extends BoundMethodBase {
    type: "creditcard";

    method: CardMethod | InvoiceMethod;
    card: BillingAccountCardWithData;
}

/** @returns The payment methods currently associated with the account */
export function useAccountMethods(billingAccount: EBillingAccount) {
    const cardMethods: BoundCardMethod[] = useSuspenseQueryAtom(accountCardMethodsAtom(billingAccount.id));
    const otherMethods: BoundOtherMethod[] = useAtomValue(accountLinkedMethodsAtom(billingAccount.account));
    return { cardMethods, otherMethods };
}

/** The `link`-type payment methods bound to a billing account */
export const accountLinkedMethodsAtom = atomFamilyWeak((account: WithAdditionalData) =>
    atom((get) => {
        const link_methods = parseAccountAdditionalData(account)?.link_methods;
        if (!link_methods || link_methods.length === 0) {
            return [];
        }

        const processors = get(processorsAtom);
        // NB: Just ignores missing methods
        const linkedProcessors = filterNulls(link_methods.map((id) => processors.get(id as PaymentMethodId)));

        return linkedProcessors.map((method): BoundOtherMethod => {
            invariant(method.type !== "creditcard", "Expected non-card method");
            return { type: "other", id: method.id, method };
        });
    }),
);

const invoiceProcessorAtom = atom((get) => {
    const processors = get(processorsAtom);
    const invoiceProcessor = processors.get("invoice");
    if (!invoiceProcessor) {
        showError("Invoice processor must be available when invoice card type is present");
        return undefined;
    }
    return invoiceProcessor as InvoiceMethod;
});

/** The `creditcard`-type payment methods bound to a billing account */
export const accountCardMethodsAtom = atomFamily((accountId: BillingAccountId) =>
    atom((get) =>
        queryThen(get(baCardQueryAtom(accountId)), (cards) => {
            if (cards.length === 0) {
                return [];
            }

            const bound = cards.map((card): BoundCardMethod | undefined => {
                const parsed = parseCardAdditionalData(card);

                let cardProcessor;
                if (parsed.parsedAdditionalData?.type === "paying_by_invoice") {
                    cardProcessor = get(invoiceProcessorAtom);
                } else if (!card.processor) {
                    // XXX: Backward compatibility path, delete when multi-processor backend is fully deployed
                    cardProcessor = get(legacy_creditCardProcessorAtom);
                } else {
                    const { keyword } = card.processor;
                    const method = get(processorsAtom).get(keyword);
                    if (method?.type !== "creditcard") {
                        showError("Card has invalid processor %s attached, skipping card ID: %s", keyword, card.id);
                        return undefined;
                    }
                    cardProcessor = method satisfies CardMethod;
                }

                if (!cardProcessor) {
                    console.warn("Card processor not available, skipping card: %o", card);
                    return undefined;
                }

                return {
                    type: "creditcard",
                    id: `__card__/${card.id}`,
                    method: cardProcessor,
                    card: parsed,
                };
            });

            return filterNulls(bound);
        }),
    ),
);

const _accountAllMethodsAtom = atomFamily((account: EBillingAccount) =>
    atom((get) => {
        const linkedMethods = get(accountLinkedMethodsAtom(account.account));
        const cardMethodsQuery = get(accountCardMethodsAtom(account.id));
        return queryThen(cardMethodsQuery, (cardMethods) => [...linkedMethods, ...cardMethods]);
    }),
);

export function getPrimaryCard(cardMethods: BoundCardMethod[]) {
    return cardMethods.find((m) => m.card.is_primary);
}

export function isUnverified(method: BoundMethod) {
    return method.type === "creditcard" && !method.card.is_verified;
}
